import React from "react";
import {
 Card,
 H1,
 H3,
 Text,
 Button,
 ControlGroup,
 Tag,
 Spinner,
 NonIdealState,
 Icon,
 InputGroup,
 Divider,
} from "@blueprintjs/core";
import SingleSelectWrapper from "../../../micro/utils/SingleSelectWrapper";
import { DateInput } from "@blueprintjs/datetime";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import { inject, observer } from "mobx-react";
import { apiCallGet, apiCallPost } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";
import moment from "moment";
import { withRouter } from "react-router-dom";

class ImplantInvoiceReport extends React.Component {
 state = {
  // Data
  implantInvoices: [],
  allImplantInvoices: [],
  branches: [],

  // Loading states
  isLoading: false,
  isGenerating: false,

  // Filters
  fromDate: new Date(),
  toDate: new Date(),
  selectedBranch: "",
  selectedBranchName: null,

  // AG Grid state
  gridApi: null,
  gridColumnApi: null,
  quickFilterText: "",
 };

 componentDidMount = async () => {
  await this.loadInitialData();
 };

 componentDidUpdate(prevProps) {
  if (
   prevProps.authStore?.selectedBranchName !==
   this.props.authStore?.selectedBranchName
  ) {
   const preferredName = this.props.authStore?.selectedBranchName;
   if (!preferredName) {
    this.setState({
     selectedBranch: "",
     selectedBranchName: null,
    });
    return;
   }
   const match = (this.state.branches || []).find(
    (b) => (b.name || "").toLowerCase() === String(preferredName).toLowerCase()
   );
   if (match && match._id !== this.state.selectedBranch) {
    this.setState({
     selectedBranch: match._id,
     selectedBranchName: match.name,
    });
   }
  }
 }

 loadInitialData = async () => {
  try {
   this.setState({ isLoading: true });
   const branches = await this.loadBranches();

   // Set default branch from global selection if available
   if (branches && branches.length > 0) {
    const preferredName = this.props.authStore?.selectedBranchName;
    const norm = (s) =>
     String(s || "")
      .trim()
      .toLowerCase();
    const match = preferredName
     ? branches.find((b) => norm(b.name) === norm(preferredName))
     : null;
    this.setState(
     {
      selectedBranch: (match && match._id) || "ALL_BRANCHES",
      selectedBranchName: (match && match.name) || null,
     },
     async () => {
      // Auto-generate report once branch is set
      await this.generateReport();
     }
    );
   } else {
    cogoToast.warn("No branches available. Please contact administrator.");
   }
  } catch (err) {
   console.error("Error loading initial data:", err);
   cogoToast.error("Failed to load initial data");
   this.setState({ implantInvoices: [] });
  } finally {
   this.setState({ isLoading: false });
  }
 };

 loadBranches = async () => {
  try {
   const response = await apiCallPost(
    `/rai/${this.props.authStore.prefix}/getAllSites`,
    {}
   );
   const rawBranches = response?.data || response || [];
   const branches = (Array.isArray(rawBranches) ? rawBranches : [])
    .map((site, idx) => ({
     _id:
      site?._id ||
      site?.id ||
      site?.siteId ||
      site?.value ||
      site?.key ||
      (typeof site === "string" ? site : String(idx)),
     name:
      site?.site ||
      site?.siteName ||
      site?.name ||
      site?.branchName ||
      site?.label ||
      (typeof site === "string" ? site : `Site ${idx + 1}`),
    }))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

   console.log("🔍 DEBUG: Branches loaded:", branches.length);
   this.setState({ branches });
   return branches;
  } catch (err) {
   console.error("Error loading branches:", err);
   try {
    const response = await apiCallPost(
     `/meta-sx/${this.props.authStore.prefix}/branchMaster/getAll`,
     {}
    );
    const rawBranches = response?.data || response || [];
    const branches = (Array.isArray(rawBranches) ? rawBranches : [])
     .map((site, idx) => ({
      _id: site?._id || site?.id || String(idx),
      name:
       site?.site ||
       site?.siteName ||
       site?.name ||
       site?.branchName ||
       site?.label ||
       `Site ${idx + 1}`,
     }))
     .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    this.setState({ branches });
    return branches;
   } catch (fallbackErr) {
    console.error("Fallback branch API also failed:", fallbackErr);
    const dummyBranches = [
     { _id: "branch1", name: "Barrackpore" },
     { _id: "branch2", name: "Kolkata" },
     { _id: "branch3", name: "Howrah" },
     { _id: "branch4", name: "Dum Dum" },
    ];
    this.setState({ branches: dummyBranches });
    return dummyBranches;
   }
  }
 };

 generateReport = async () => {
  try {
   this.setState({ isGenerating: true });

   // Build payload with filters
   const payload = {
    startDate: moment(this.state.fromDate).format("YYYY-MM-DD"),
    endDate: moment(this.state.toDate).format("YYYY-MM-DD"),
   };

   // Only add branch filter if a specific branch is selected
   if (
    this.state.selectedBranch &&
    this.state.selectedBranch !== "ALL_BRANCHES"
   ) {
    const selectedBranchObj = this.state.branches.find(
     (b) => b._id === this.state.selectedBranch
    );
    const branchName = selectedBranchObj?.name;
    if (branchName) {
     payload.branchName = branchName;
    }
   }

   console.log(
    "🔍 DEBUG: Generating Implant Invoice Report with payload:",
    payload
   );

   // Fetch surgeries with implant invoices from getSurgeries API only
   const bookingsEndpoint = `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getSurgeries`;
   const fullPayload = {
    startDate: payload.startDate,
    endDate: payload.endDate,
    branchName: payload.branchName,
    page: 1,
    sortBy: "scheduledDate",
    sortOrder: "desc",
   };

   console.log(
    "🔍 DEBUG: POST Endpoint:",
    bookingsEndpoint,
    "Payload:",
    fullPayload
   );
   const resp = await apiCallPost(bookingsEndpoint, fullPayload);

   console.log("🔍 DEBUG: Surgeries API Response:", resp);

   const bookings = Array.isArray(resp?.data?.items)
    ? resp.data.items
    : Array.isArray(resp?.data)
    ? resp.data
    : [];

   console.log("🔍 DEBUG: Extracted bookings:", bookings.length);

   // Extract implant invoices from bookings and create report rows
   const implantRows = [];

   bookings.forEach((booking) => {
    // Check if booking has implant invoices array
    const implantInvoices = booking?.implantInvoices || [];

    if (implantInvoices.length > 0) {
     console.log(
      `🔍 DEBUG: Booking ${booking.bookingReference} has ${implantInvoices.length} implant invoices`
     );

     // Get only the last generated invoice (highest counter or latest generatedAt)
     const lastInvoice = implantInvoices.reduce((latest, current) => {
      // First check counter field
      if (current.counter && latest.counter) {
       return current.counter > latest.counter ? current : latest;
      }
      // Fallback to generatedAt timestamp
      const currentDate = new Date(current.generatedAt || 0);
      const latestDate = new Date(latest.generatedAt || 0);
      return currentDate > latestDate ? current : latest;
     });

     console.log(
      `🔍 DEBUG: Using invoice #${lastInvoice.counter || "N/A"} (${
       lastInvoice.number
      }) for ${booking.bookingReference}`
     );

     // Process only the last invoice
     const items = lastInvoice.itemsSnapshot || [];
     items.forEach((item, itemIndex) => {
      implantRows.push(
       this.mapInvoiceItemToRow(lastInvoice, item, booking, itemIndex)
      );
     });
    }
   });

   console.log("🔍 DEBUG: Mapped implant rows:", implantRows.length);

   this.setState({
    allImplantInvoices: implantRows,
    implantInvoices: implantRows,
   });

   if (implantRows.length > 0) {
    cogoToast.success(
     `Report generated • ${implantRows.length} implant records found`
    );
   } else {
    cogoToast.warn("No implant invoice data found for the selected criteria");
   }
  } catch (err) {
   console.error("Error generating Implant Invoice Report:", err);
   cogoToast.error(
    `Failed to generate report: ${err?.message || "Unknown error"}`
   );
   this.setState({ implantInvoices: [], allImplantInvoices: [] });
  } finally {
   this.setState({ isGenerating: false });
  }
 };

 mapInvoiceItemToRow = (invoice, item, booking, itemIndex) => {
  // Helper to convert paise to rupees
  const paiseToRupees = (paise) => {
   const num = Number(paise || 0);
   return num / 100;
  };

  // Extract implant item details from invoice itemsSnapshot
  const itemName = item?.itemName || "-";
  const hsnCode = item?.hsnCode || "90213900";
  const serialNo = item?.serialNo || item?.batchNo || "-";
  const expiry = item?.expiry || "-";
  const qty = item?.qty || item?.quantity || 1;
  const price = paiseToRupees(item?.pricePaise || item?.rate || 0);
  const cgstPercent = item?.cgst || 0;
  const sgstPercent = item?.sgst || 0;
  const total = paiseToRupees(item?.totalPaise || item?.totalAmount || 0);

  // Calculate GST amounts
  // GST Amount = (Price × GST%) / 100
  const cgstAmount = (price * cgstPercent) / 100;
  const sgstAmount = (price * sgstPercent) / 100;
  const totalGst = cgstAmount + sgstAmount;

  // Get procedure name from booking's selected surgeries
  const procedures = (booking?.selectedSurgeries || [])
   .map((s) => s.surgeryName || s.name || s.procedure)
   .filter(Boolean)
   .join(", ");

  // Use invoice generatedAt as the date if booking date is not available
  const date = booking?.scheduledDate
   ? moment(booking.scheduledDate).format("DD-MMM-YYYY")
   : invoice?.generatedAt
   ? moment(invoice.generatedAt).format("DD-MMM-YYYY")
   : "-";

  // Get latest authorization for TPA/Insurance details
  const latestAuth = this.getLatestAuthorization(booking);

  // Get latest surgery invoice (not implant invoice)
  const latestSurgeryInvoice = this.getLatestSurgeryInvoice(booking);

  return {
   _id: `${invoice._id}-${itemIndex}`,
   "Sx ID": invoice.bookingReference || "-",
   "Patient Name": booking?.patientName || "-",
   Branch: booking?.branchName || "-",
   Date: date,
   Procedure: procedures || "-",
   TPA: latestAuth?.tpaName || "-",
   Insurance: latestAuth?.insuranceName || "-",
   "CC No": latestAuth?.ccn || "-",
   "Claim Payor": latestAuth?.claimPayor || "-",
   ITEM: itemName,
   "HSN Code": hsnCode,
   "Serial No / Batch No": serialNo,
   EXP: expiry,
   QTY: qty,
   PRICE: price,
   "CGST(%)": cgstPercent,
   "CGST Amt": cgstAmount,
   "SGST(%)": sgstPercent,
   "SGST Amt": sgstAmount,
   "Total GST": totalGst,
   TOTAL: total,
   "Surgery Invoice No": latestSurgeryInvoice?.number || "-",
   "Implant Invoice No": invoice.number,
   "Generated By": invoice.generatedBy?.name || "-",

   // Additional fields for filtering
   _originalInvoice: invoice,
   _originalBooking: booking,
   _originalItem: item,
   _branchId: booking?.branchId || "",
   _branchName: booking?.branchName || "",
   _scheduledDate: booking?.scheduledDate || invoice?.generatedAt,
  };
 };

 getLatestAuthorization = (booking) => {
  const entries = booking?.authorization?.entries || [];
  if (!Array.isArray(entries) || entries.length === 0) {
   return null;
  }

  const activeIndex = booking?.authorization?.activeIndex;
  if (
   typeof activeIndex === "number" &&
   activeIndex >= 0 &&
   activeIndex < entries.length
  ) {
   return entries[activeIndex];
  }

  // If no active index, return the most recent entry
  return entries.sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0))[0];
 };

 getLatestSurgeryInvoice = (booking) => {
  const invoices = booking?.invoices || [];
  if (!Array.isArray(invoices) || invoices.length === 0) {
   return null;
  }

  // Return the most recent invoice
  return invoices.sort(
   (a, b) => new Date(b.generatedAt || 0) - new Date(a.generatedAt || 0)
  )[0];
 };

 handleDateChange = (field, date) => {
  this.setState({ [field]: date });
 };

 createBranchSelectItems = () => {
  return this.state.branches.map((branch) => branch.name).filter(Boolean);
 };

 handleBranchFilterSelect = (branchName) => {
  if (branchName === null || branchName === undefined) {
   this.setState({
    selectedBranch: "ALL_BRANCHES",
    selectedBranchName: null,
   });
  } else {
   const selectedBranch = this.state.branches.find(
    (branch) => branch.name === branchName
   );
   this.setState({
    selectedBranch: selectedBranch ? selectedBranch._id : "",
    selectedBranchName: branchName || null,
   });
  }
 };

 clearFilters = () => {
  this.setState({
   fromDate: new Date(),
   toDate: new Date(),
   quickFilterText: "",
  });
  cogoToast.success("Filters cleared (click Generate to apply)");
 };

 hasActiveFilters = () => {
  const { fromDate, toDate } = this.state;
  const today = moment().format("YYYY-MM-DD");
  const fromDateStr = moment(fromDate).format("YYYY-MM-DD");
  const toDateStr = moment(toDate).format("YYYY-MM-DD");
  return fromDateStr !== today || toDateStr !== today;
 };

 calculateTotals = (data) => {
  const numericFields = [
   "QTY",
   "PRICE",
   "CGST(%)",
   "CGST Amt",
   "SGST(%)",
   "SGST Amt",
   "Total GST",
   "TOTAL",
  ];
  const totals = {};

  numericFields.forEach((field) => {
   totals[field] = 0;
  });

  (data || []).forEach((row) => {
   numericFields.forEach((field) => {
    const value = Number(row?.[field] || 0);
    if (!isNaN(value)) {
     totals[field] += value;
    }
   });
  });

  return totals;
 };

 formatCurrencyForTotal = (amount) => {
  return `₹${Number(amount || 0).toLocaleString("en-IN")}`;
 };

 getColumnDefs = () => {
  const currencyFormatter = (params) => {
   const value = Number(params.value || 0);
   return `₹${value.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
   })}`;
  };

  const percentFormatter = (params) => {
   const value = Number(params.value || 0);
   return `${value}%`;
  };

  return [
   {
    headerName: "Sx ID",
    field: "Sx ID",
    width: 140,
    pinned: "left",
    cellStyle: { fontWeight: "600" },
    cellRenderer: (params) => {
     const value = params.value;
     if (!value || value === "-") return "-";

     const container = document.createElement("div");
     const link = document.createElement("span");

     link.innerHTML = value;
     link.style.color = "#137cbd";
     link.style.cursor = "pointer";
     link.style.textDecoration = "underline";
     link.style.fontWeight = "600";
     link.onclick = () => this.navigateToDetails(params.data);

     container.appendChild(link);
     return container;
    },
   },
   {
    headerName: "Patient Name",
    field: "Patient Name",
    width: 180,
    pinned: "left",
   },
   {
    headerName: "Branch",
    field: "Branch",
    width: 150,
   },
   {
    headerName: "Date",
    field: "Date",
    width: 130,
   },
   {
    headerName: "Procedure",
    field: "Procedure",
    width: 200,
   },
   {
    headerName: "TPA",
    field: "TPA",
    width: 150,
   },
   {
    headerName: "Insurance",
    field: "Insurance",
    width: 150,
   },
   {
    headerName: "CC No",
    field: "CC No",
    width: 130,
   },
   {
    headerName: "Claim Payor",
    field: "Claim Payor",
    width: 150,
   },
   {
    headerName: "ITEM",
    field: "ITEM",
    width: 200,
   },
   {
    headerName: "HSN Code",
    field: "HSN Code",
    width: 120,
   },
   {
    headerName: "Serial No / Batch No",
    field: "Serial No / Batch No",
    width: 150,
   },
   {
    headerName: "EXP",
    field: "EXP",
    width: 120,
   },
   {
    headerName: "QTY",
    field: "QTY",
    width: 100,
    type: "numericColumn",
   },
   {
    headerName: "PRICE",
    field: "PRICE",
    width: 120,
    type: "numericColumn",
    valueFormatter: currencyFormatter,
   },
   {
    headerName: "CGST(%)",
    field: "CGST(%)",
    width: 100,
    type: "numericColumn",
    valueFormatter: percentFormatter,
   },
   {
    headerName: "CGST Amt",
    field: "CGST Amt",
    width: 120,
    type: "numericColumn",
    valueFormatter: currencyFormatter,
   },
   {
    headerName: "SGST(%)",
    field: "SGST(%)",
    width: 100,
    type: "numericColumn",
    valueFormatter: percentFormatter,
   },
   {
    headerName: "SGST Amt",
    field: "SGST Amt",
    width: 120,
    type: "numericColumn",
    valueFormatter: currencyFormatter,
   },
   {
    headerName: "Total GST",
    field: "Total GST",
    width: 130,
    type: "numericColumn",
    valueFormatter: currencyFormatter,
    cellStyle: {
     fontWeight: "600",
     backgroundColor: "rgba(19, 124, 189, 0.1)",
    },
   },
   {
    headerName: "TOTAL",
    field: "TOTAL",
    width: 140,
    type: "numericColumn",
    valueFormatter: currencyFormatter,
    cellStyle: { fontWeight: "600" },
   },
   {
    headerName: "Surgery Invoice No",
    field: "Surgery Invoice No",
    width: 200,
    cellStyle: { fontSize: "11px" },
   },
   {
    headerName: "Implant Invoice No",
    field: "Implant Invoice No",
    width: 200,
    cellStyle: { fontSize: "11px", fontWeight: "600" },
   },
   {
    headerName: "Generated By",
    field: "Generated By",
    width: 150,
   },
  ];
 };

 navigateToDetails = (row) => {
  const booking = row._originalBooking;
  const detailsPath = `/v3/workflow/surgery-booking/${
   booking._id || booking.docNo || booking.bookingReference
  }`;
  window.open(detailsPath, "_blank", "noopener,noreferrer");
 };

 onGridReady = (params) => {
  this.setState({
   gridApi: params.api,
   gridColumnApi: params.columnApi,
  });
 };

 getDisplayedColumns = () => {
  if (!this.state.gridColumnApi) return this.getColumnDefs();
  const allColumns = this.state.gridColumnApi.getAllColumns();
  if (!allColumns) return this.getColumnDefs();
  const visibleColumns = allColumns.filter((col) => col.isVisible());
  return visibleColumns.map((col) => {
   const colDef = col.getColDef();
   return {
    headerName: colDef.headerName,
    field: colDef.field,
    ...colDef,
   };
  });
 };

 onQuickFilterChanged = (event) => {
  this.setState({ quickFilterText: event.target.value });
 };

 exportToCSV = () => {
  let data = [];
  if (this.state.gridApi) {
   this.state.gridApi.forEachNodeAfterFilter((node) => {
    data.push(node.data);
   });
  } else {
   data = this.state.implantInvoices || [];
  }

  const columns = this.getDisplayedColumns();
  const totals = this.calculateTotals(data);

  const headers = columns.map((c) => c.headerName).join(",");

  const bodyRows = (data || [])
   .map((row) => {
    return columns
     .map((col) => {
      let value = row[col.field];
      const s =
       value === null || typeof value === "undefined" ? "" : `${value}`;
      const escaped = s.replace(/"/g, '""');
      return `"${escaped}"`;
     })
     .join(",");
   })
   .join("\n");

  const totalsRow = columns
   .map((col) => {
    const field = col.field;
    if (Object.prototype.hasOwnProperty.call(totals, field)) {
     return `${totals[field].toLocaleString("en-IN")}`;
    }
    if (field === "Sx ID") {
     return "TOTAL";
    }
    return "";
   })
   .join(",");

  const csvContent = `${headers}\n${bodyRows}${
   bodyRows ? "\n" : ""
  }${totalsRow}`;

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute(
   "download",
   `implant-invoice-report-${moment().format("YYYYMMDD-HHmm")}.csv`
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  cogoToast.success("CSV exported successfully with totals!");
 };

 exportToPDF = () => {
  let filteredData = [];
  if (this.state.gridApi) {
   this.state.gridApi.forEachNodeAfterFilter((node) => {
    filteredData.push(node.data);
   });
  } else {
   filteredData = this.state.implantInvoices || [];
  }

  const columns = this.getDisplayedColumns();
  const rows = filteredData;

  const branchName =
   this.state.selectedBranch === "ALL_BRANCHES"
    ? "All Branches"
    : this.state.branches.find((b) => b._id === this.state.selectedBranch)
       ?.name || "All Branches";

  const win = window.open("", "_blank");
  if (!win) {
   cogoToast.error("Popup blocked. Allow popups to export PDF");
   return;
  }

  const styles = `
      <style>
        @page { size: A4 landscape; margin: 0.5in; }
        body { font-family: Arial, sans-serif; padding: 8px; margin: 0; }
        h2 { margin: 0 0 8px 0; font-size: 16px; }
        .meta { color: #555; margin-bottom: 12px; font-size: 12px; }
        table { width: 100%; border-collapse: collapse; font-size: 9px; }
        th, td { border: 1px solid #999; padding: 4px; text-align: left; vertical-align: top; }
        th { background: #eee; font-weight: bold; }
        .currency { text-align: right; }
        .wrap { word-wrap: break-word; max-width: 120px; }
      </style>
    `;

  const headerHtml = `
      <h2>Implant Invoice Report</h2>
      <div class="meta">${branchName} - ${moment(this.state.fromDate).format(
   "DD-MMM-YYYY"
  )} to ${moment(this.state.toDate).format("DD-MMM-YYYY")} | ${
   rows.length
  } records</div>
    `;

  const thead = `<tr>${columns
   .map((c) => `<th>${c.headerName}</th>`)
   .join("")}</tr>`;
  const tbody = rows
   .map(
    (r) =>
     `<tr>${columns
      .map((c) => {
       const v = r[c.field];
       const value = v === null || typeof v === "undefined" ? "" : v;
       let className = "";
       if (
        c.type === "numericColumn" ||
        c.field === "PRICE" ||
        c.field === "CGST Amt" ||
        c.field === "SGST Amt" ||
        c.field === "Total GST" ||
        c.field === "TOTAL"
       ) {
        className = "currency";
       }
       return `<td class="${className.trim()}">${value}</td>`;
      })
      .join("")}</tr>`
   )
   .join("");

  const totals = this.calculateTotals(rows);
  const totalsRow = `<tr style="background: #f0f0f0; font-weight: bold;">${columns
   .map((c) => {
    const field = c.field;
    if (field === "Sx ID") {
     return `<td style="font-weight: bold;">TOTAL</td>`;
    }
    if (Object.prototype.hasOwnProperty.call(totals, field)) {
     // Format currency for price and total columns
     if (
      field === "PRICE" ||
      field === "CGST Amt" ||
      field === "SGST Amt" ||
      field === "Total GST" ||
      field === "TOTAL"
     ) {
      return `<td style="font-weight: bold; text-align: right;">${this.formatCurrencyForTotal(
       totals[field]
      )}</td>`;
     }
     return `<td style="font-weight: bold; text-align: right;">${totals[
      field
     ].toLocaleString("en-IN")}</td>`;
    }
    return `<td></td>`;
   })
   .join("")}</tr>`;

  win.document.write(
   `<!doctype html><html><head><meta charset="utf-8"/>${styles}</head><body>${headerHtml}<table>${thead}${tbody}${totalsRow}</table></body></html>`
  );
  win.document.close();
  win.focus();
  win.print();
  cogoToast.success("PDF opened for printing");
 };

 renderFilters = () => {
  return (
   <Card
    elevation={2}
    style={{
     padding: "20px",
     backgroundColor: "var(--bg-surface)",
     border: "1px solid var(--border-color)",
     marginBottom: "20px",
    }}
   >
    <div
     style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}
    >
     <Icon
      icon="filter"
      style={{ marginRight: "10px", color: "var(--text-primary)" }}
     />
     <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Filters</H3>
     <div
      style={{
       marginLeft: "auto",
       display: "flex",
       alignItems: "center",
       gap: "10px",
      }}
     >
      <Tag intent="success" large>
       {this.state.implantInvoices.length} Records
      </Tag>
     </div>
    </div>

    {/* First row - Main filters */}
    <div
     style={{
      display: "flex",
      alignItems: "center",
      gap: "15px",
      flexWrap: "wrap",
      marginBottom: "15px",
     }}
    >
     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
       From
      </Tag>
      <DateInput
       value={this.state.fromDate}
       onChange={(d) => this.handleDateChange("fromDate", d)}
       formatDate={(date) => moment(date).format("DD-MMM-YYYY")}
       parseDate={(str) => moment(str, "DD-MMM-YYYY").toDate()}
       placeholder="From Date"
       style={{ width: "140px" }}
       rightElement={<Icon icon="calendar" size={14} />}
      />
     </div>

     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
       To
      </Tag>
      <DateInput
       value={this.state.toDate}
       onChange={(d) => this.handleDateChange("toDate", d)}
       formatDate={(date) => moment(date).format("DD-MMM-YYYY")}
       parseDate={(str) => moment(str, "DD-MMM-YYYY").toDate()}
       placeholder="To Date"
       style={{ width: "140px" }}
       rightElement={<Icon icon="calendar" size={14} />}
      />
     </div>

     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
       Branch *
      </Tag>
      <div style={{ width: "160px" }}>
       <SingleSelectWrapper
        items={[
         `All Branches (${this.state.branches.length})`,
         ...this.createBranchSelectItems(),
        ]}
        selectedItem={
         this.state.selectedBranchName ||
         `All Branches (${this.state.branches.length})`
        }
        placeholder="Search branches..."
        onChange={(value) => {
         if (value === `All Branches (${this.state.branches.length})`) {
          this.handleBranchFilterSelect(null);
         } else {
          this.handleBranchFilterSelect(value);
         }
        }}
        width="100%"
       />
      </div>
     </div>
    </div>

    {/* Second row - Quick Filter and Actions */}
    <div
     style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "15px",
     }}
    >
     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
       Quick Filter
      </Tag>
      <InputGroup
       placeholder="Search all columns..."
       value={this.state.quickFilterText}
       onChange={this.onQuickFilterChanged}
       leftIcon="search"
       style={{ width: "300px" }}
       small
      />
     </div>

     <div style={{ display: "flex", gap: "8px" }}>
      <Button
       intent="primary"
       icon="search"
       onClick={this.generateReport}
       loading={this.state.isGenerating}
      >
       Generate
      </Button>
      <Button
       icon="filter-remove"
       onClick={this.clearFilters}
       disabled={!this.hasActiveFilters()}
      >
       Clear Filters
      </Button>
      <Button
       icon="document"
       onClick={this.exportToPDF}
       disabled={!this.state.implantInvoices.length}
      >
       PDF
      </Button>
      <Button
       icon="th"
       onClick={this.exportToCSV}
       disabled={!this.state.implantInvoices.length}
      >
       CSV
      </Button>
     </div>
    </div>
   </Card>
  );
 };

 renderTable = () => {
  if (this.state.isGenerating) {
   return (
    <NonIdealState
     icon={<Spinner />}
     title="Generating Report..."
     description="Please wait while we fetch the implant invoice data"
    />
   );
  }

  if (!this.state.implantInvoices || this.state.implantInvoices.length === 0) {
   return (
    <div
     style={{
      textAlign: "center",
      padding: "40px",
      color: "var(--text-secondary)",
      backgroundColor: "var(--bg-secondary)",
      borderRadius: "8px",
      border: "1px solid var(--border-color)",
     }}
    >
     <Icon icon="th" size={48} style={{ marginBottom: "15px", opacity: 0.5 }} />
     <div style={{ fontSize: "16px", fontWeight: "500", marginBottom: "8px" }}>
      No implant records found
     </div>
     <div style={{ fontSize: "14px", opacity: 0.8 }}>
      Try adjusting your date range or filters
     </div>
    </div>
   );
  }

  return (
   <div
    className="ag-theme-alpine-dark"
    style={{
     height: "600px",
     width: "100%",
     borderRadius: "8px",
     border: "1px solid var(--border-color)",
     backgroundColor: "var(--bg-surface)",
    }}
   >
    <AgGridReact
     rowData={this.state.implantInvoices}
     columnDefs={this.getColumnDefs()}
     onGridReady={this.onGridReady}
     quickFilterText={this.state.quickFilterText}
     animateRows={true}
     rowSelection="multiple"
     suppressRowClickSelection={true}
     enableBrowserTooltips={true}
     enableRangeSelection={true}
     enableCellTextSelection={true}
     sideBar={{
      toolPanels: [
       {
        id: "columns",
        labelDefault: "Columns",
        labelKey: "columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
        toolPanelParams: {
         suppressRowGroups: true,
         suppressValues: true,
         suppressPivots: true,
         suppressPivotMode: true,
         suppressColumnFilter: false,
         suppressColumnSelectAll: false,
         suppressColumnExpandAll: false,
        },
       },
       {
        id: "filters",
        labelDefault: "Filters",
        labelKey: "filters",
        iconKey: "filter",
        toolPanel: "agFiltersToolPanel",
        toolPanelParams: {
         suppressExpandAll: false,
         suppressFilterSearch: false,
        },
       },
      ],
      defaultToolPanel: null,
      hiddenByDefault: false,
      position: "right",
     }}
     defaultColDef={{
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 80,
      menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
     }}
     sortingOrder={["desc", "asc"]}
     pagination={true}
     paginationPageSize={50}
     paginationPageSizeSelector={[25, 50, 100, 200]}
     suppressPaginationPanel={false}
     domLayout="normal"
     getRowId={(params) => params.data._id || Math.random()}
     rowHeight={45}
     headerHeight={40}
    />
   </div>
  );
 };

 renderHeader = () => {
  const branchName =
   this.state.selectedBranch === "ALL_BRANCHES"
    ? "All Branches"
    : this.state.branches.find((b) => b._id === this.state.selectedBranch)
       ?.name || "All Branches";

  return (
   <div
    style={{
     textAlign: "center",
     marginBottom: "20px",
     borderBottom: "2px solid var(--border-color)",
     paddingBottom: "12px",
    }}
   >
    <H1 style={{ color: "var(--text-primary)", margin: 0, fontSize: "24px" }}>
     IMPLANT INVOICE REPORT
    </H1>
    <Text
     style={{
      color: "var(--text-secondary)",
      margin: "5px 0",
      fontSize: "16px",
     }}
    >
     {branchName} - {moment(this.state.fromDate).format("DD-MMM-YYYY")} to{" "}
     {moment(this.state.toDate).format("DD-MMM-YYYY")}
    </Text>
   </div>
  );
 };

 render() {
  if (this.state.isLoading) {
   return (
    <NonIdealState
     icon={<Spinner />}
     title="Loading..."
     description="Preparing filters and data"
    />
   );
  }

  return (
   <div
    style={{
     padding: "20px",
     backgroundColor: "var(--bg-primary)",
     color: "var(--text-primary)",
     minHeight: "100vh",
    }}
   >
    <div style={{ marginBottom: "20px" }}>
     <div style={{ display: "flex", alignItems: "center" }}>
      <H1 style={{ color: "var(--text-primary)", margin: 0 }}>
       Implant Invoice Report
      </H1>
     </div>
     <Text
      style={{
       color: "var(--text-secondary)",
       margin: "5px 0",
       fontSize: "16px",
      }}
     >
      Comprehensive report of all implant/IOL invoices with detailed item-level
      information
     </Text>
    </div>

    {this.renderFilters()}

    <Card
     elevation={2}
     style={{
      padding: "20px",
      backgroundColor: "var(--bg-surface)",
      border: "1px solid var(--border-color)",
     }}
    >
     {this.renderHeader()}
     {this.renderTable()}
    </Card>
   </div>
  );
 }
}

export default inject("authStore")(observer(withRouter(ImplantInvoiceReport)));
